import { useEffect, useMemo, useState } from 'react';
import { api, type Category, type Radar } from '../api';
import { Scope } from './Scope';

/**
 * Radar editor.
 *
 * The scope is the control, not an illustration: dragging the range slider
 * grows the ring, and the preview count underneath answers the only question
 * that matters when tuning a radius — "how much will this actually buzz me?"
 */
export function RadarPanel({
  radar,
  categories,
  onClose,
  onSaved,
}: {
  radar: Radar | null;
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = radar !== null;

  const [name, setName] = useState(radar?.name ?? 'New radar');
  const [lat, setLat] = useState(radar?.lat ?? 40.758);
  const [lng, setLng] = useState(radar?.lng ?? -73.9855);
  const [radiusM, setRadiusM] = useState(radar?.radiusM ?? 16_000);
  const [includeOnline, setIncludeOnline] = useState(radar?.includeOnline ?? true);
  const [selected, setSelected] = useState<string[]>(radar?.categories ?? []);
  const [notify, setNotify] = useState(radar?.notify ?? true);
  const [liveActivity, setLiveActivity] = useState(radar?.liveActivity ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ estimatedPerDay: number } | null>(null);

  // Preview is debounced: the slider fires continuously and each preview is a
  // real query.
  useEffect(() => {
    const timer = setTimeout(() => {
      api
        .previewRadar({ name, lat, lng, radiusM, includeOnline, categories: selected })
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [name, lat, lng, radiusM, includeOnline, selected]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const miles = useMemo(() => (radiusM / 1609.344).toFixed(radiusM < 4000 ? 1 : 0), [radiusM]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        name,
        lat,
        lng,
        radiusM,
        includeOnline,
        categories: selected,
        notify,
        liveActivity,
      };
      if (editing) await api.updateRadar(radar.id, body);
      else await api.createRadar({ ...body, kind: 'drop' });
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save radar');
    } finally {
      setBusy(false);
    }
  }

  async function useMyLocation() {
    if (!navigator.geolocation) {
      setError('This browser cannot share your location.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(Number(pos.coords.latitude.toFixed(5)));
        setLng(Number(pos.coords.longitude.toFixed(5)));
      },
      () => setError('Location access was declined. Enter coordinates instead.'),
    );
  }

  async function remove() {
    if (!radar) return;
    setBusy(true);
    try {
      await api.deleteRadar(radar.id);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete radar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="panel" role="dialog" aria-modal="true" aria-label="Radar settings">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="tag-cat" style={{ color: 'var(--field-text-dim)' }}>
            {editing ? `Radar · ${radar.kind}` : 'New drop radar'}
          </span>
          <button className="panel-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={{ display: 'grid', placeItems: 'center', margin: '18px 0 4px' }}>
          <Scope radiusM={radiusM} scaleM={radiusM * 2} size={240} />
        </div>

        <p
          style={{
            textAlign: 'center',
            fontFamily: 'var(--mono)',
            color: 'var(--field-text-dim)',
            fontSize: 12,
            margin: '0 0 4px',
          }}
        >
          {miles} mile radius
          {preview && <> · about {preview.estimatedPerDay} deals a day</>}
        </p>

        {error && <p className="error">{error}</p>}

        <label className="form-label" htmlFor="radar-name">
          Name
        </label>
        <input
          id="radar-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
        />

        <label className="form-label" htmlFor="radar-range">
          Range
        </label>
        <input
          id="radar-range"
          className="range"
          type="range"
          min={800}
          max={80_000}
          step={800}
          value={radiusM}
          onChange={(e) => setRadiusM(Number(e.target.value))}
        />

        <label className="form-label">Centre</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="input"
            type="number"
            step="0.00001"
            value={lat}
            aria-label="Latitude"
            onChange={(e) => setLat(Number(e.target.value))}
          />
          <input
            className="input"
            type="number"
            step="0.00001"
            value={lng}
            aria-label="Longitude"
            onChange={(e) => setLng(Number(e.target.value))}
          />
        </div>
        <button className="link" style={{ marginTop: 8 }} onClick={useMyLocation}>
          Use my current location
        </button>

        <label className="form-label">Categories</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {categories.map((c) => {
            const on = selected.includes(c.id);
            return (
              <button
                key={c.id}
                className="chip"
                aria-pressed={on}
                onClick={() =>
                  setSelected(on ? selected.filter((x) => x !== c.id) : [...selected, c.id])
                }
              >
                {c.emoji} {c.label}
              </button>
            );
          })}
        </div>
        <p style={{ fontSize: 12, color: 'var(--field-text-dim)', marginTop: 8 }}>
          {selected.length === 0 ? 'Watching every category.' : `Watching ${selected.length}.`}
        </p>

        <label className="form-label">Alerts</label>
        <Toggle checked={notify} onChange={setNotify} label="Notify me when something matches" />
        <Toggle
          checked={includeOnline}
          onChange={setIncludeOnline}
          label="Include online-only deals"
        />
        <Toggle
          checked={liveActivity}
          onChange={setLiveActivity}
          label="Start a Live Activity on iPhone"
        />

        <div className="btn-row">
          <button className="btn" onClick={save} disabled={busy}>
            {editing ? 'Save radar' : 'Create radar'}
          </button>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {editing && radar.kind !== 'home' && (
            <button className="btn btn-ghost" onClick={remove} disabled={busy}>
              Delete
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0', fontSize: 13 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
